import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, RefreshCw, Eye, MoreVertical, MapPin, Calendar, DollarSign, User, Filter, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import DataTable from '@/components/ui/data-table';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useConstantsContext } from '@/contexts/ConstantsContext';
import { constantsUtils } from '@/hooks/useConstants';

const OperationsRequestsList = ({ onStatisticsUpdate, constants: constantsProp }) => {
  const navigate = useNavigate();
  // Utiliser les constantes passées en prop, sinon utiliser le contexte
  const constantsContext = useConstantsContext();
  const constants = constantsProp || constantsContext?.constants;
  const [searchTerm, setSearchTerm] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [updateDialog, setUpdateDialog] = useState({ open: false, request: null, newStatus: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, request: null });
  const isMountedRef = useRef(true);

  const loadRequests = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (clientFilter !== 'all') params.client = clientFilter;

      const response = await adminAPI.getAllRequests(params);
      if (!isMountedRef.current) return;

      const requestsData = response.data.results || response.data || [];
      setRequests(requestsData);

      if (onStatisticsUpdate && isMountedRef.current) {
        const stats = {
          total: requestsData.length,
          pending: requestsData.filter(r => r.status === 'PUBLISHED' || r.status === 'DRAFT').length,
          assigned: requestsData.filter(r => r.status === 'ASSIGNED').length,
          in_transit: requestsData.filter(r => r.status === 'IN_TRANSIT').length,
          completed: requestsData.filter(r => r.status === 'COMPLETED').length,
          cancelled: requestsData.filter(r => r.status === 'CANCELLED').length,
        };
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Erreur lors du chargement des demandes:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les demandes',
        variant: 'destructive',
      });
      setRequests([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, clientFilter]);

  useEffect(() => {
    isMountedRef.current = true;
    loadRequests();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, clientFilter]);

  const handleUpdateStatus = async () => {
    if (!updateDialog.request || !updateDialog.newStatus) return;

    try {
      await adminAPI.updateRequestStatus(updateDialog.request.id, updateDialog.newStatus);
      toast({
        title: 'Succès',
        description: 'Statut de la demande mis à jour',
      });
      setUpdateDialog({ open: false, request: null, newStatus: '' });
      loadRequests();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de mettre à jour le statut',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteRequest = async () => {
    if (!deleteDialog.request) return;

    try {
      await adminAPI.deleteRequest(deleteDialog.request.id);
      toast({
        title: 'Succès',
        description: 'Demande supprimée avec succès',
      });
      setDeleteDialog({ open: false, request: null });
      loadRequests();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de supprimer la demande',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status) => {
    if (!constants) return <Badge>{status}</Badge>;
    const label = constantsUtils.getLabel(constants.transport_request_status, status);
    const statusConfig = {
      'DRAFT': { color: 'bg-gray-500', icon: FileText },
      'PUBLISHED': { color: 'bg-blue-500', icon: FileText },
      'ASSIGNED': { color: 'bg-green-500', icon: FileText },
      'IN_TRANSIT': { color: 'bg-purple-500', icon: FileText },
      'COMPLETED': { color: 'bg-gray-500', icon: FileText },
      'CANCELLED': { color: 'bg-red-500', icon: FileText },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500', icon: FileText };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const filteredRequests = requests.filter(request => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fromLocation = request.from_location_detail || request.from_location;
    const toLocation = request.to_location_detail || request.to_location;
    return (
      request.id?.toLowerCase().includes(searchLower) ||
      request.logiconnect_id?.toLowerCase().includes(searchLower) ||
      request.client_detail?.company_name?.toLowerCase().includes(searchLower) ||
      fromLocation?.city?.toLowerCase().includes(searchLower) ||
      fromLocation?.label?.toLowerCase().includes(searchLower) ||
      toLocation?.city?.toLowerCase().includes(searchLower) ||
      toLocation?.label?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      id: 'order',
      header: 'N°',
      accessorKey: 'order',
      cell: ({ row, table }) => {
        // Calculer le numéro d'ordre basé sur la pagination
        const pageIndex = table.getState().pagination.pageIndex || 0;
        const pageSize = table.getState().pagination.pageSize || 10;
        const rowIndex = row.index;
        const orderNumber = pageIndex * pageSize + rowIndex + 1;
        return (
          <div className="flex items-center justify-center w-12">
            <span className="text-sm font-medium text-muted-foreground">{orderNumber}</span>
          </div>
        );
      },
    },
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'client',
      cell: ({ row }) => {
        const request = row.original;
        const client = request.client_detail || request.client;

        // Debug: log pour voir la structure des données
        if (!client && process.env.NODE_ENV === 'development') {
          console.log('Request without client:', request);
        }

        // Extraire les informations du client
        let clientName = 'N/A';
        let clientEmail = '';

        if (client) {
          // Le serializer utilise user_detail au lieu de user
          const user = client.user_detail || client.user;

          // Essayer d'abord le nom de l'entreprise
          if (client.company_name) {
            clientName = client.company_name;
          }
          // Sinon, essayer le nom complet de l'utilisateur
          else if (user) {
            const firstName = user.first_name || '';
            const lastName = user.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();
            if (fullName) {
              clientName = fullName;
            } else if (user.email) {
              clientName = user.email.split('@')[0];
            }
          }
          // Sinon, essayer directement sur client si c'est un objet user
          else if (client.first_name || client.last_name) {
            const firstName = client.first_name || '';
            const lastName = client.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();
            if (fullName) {
              clientName = fullName;
            }
          }
          // Sinon, essayer l'email directement
          else if (client.email) {
            clientName = client.email.split('@')[0];
          }

          // Extraire l'email
          if (user?.email) {
            clientEmail = user.email;
          } else if (client.email) {
            clientEmail = client.email;
          }
        } else {
          // Si client est null/undefined, essayer de récupérer depuis request.client si c'est un ID
          // et afficher au moins l'ID
          if (request.client && typeof request.client === 'string') {
            clientName = `Client ID: ${request.client.slice(-8)}`;
          }
        }

        return (
          <div className="text-sm">
            <p className="font-medium">{clientName}</p>
            {clientEmail && (
              <p className="text-xs text-muted-foreground">{clientEmail}</p>
            )}
          </div>
        );
      },
    },
    {
      id: 'route',
      header: 'Trajet',
      accessorKey: 'route',
      cell: ({ row }) => {
        const request = row.original;
        const fromLocation = request.from_location_detail || request.from_location;
        const toLocation = request.to_location_detail || request.to_location;
        const fromCity = fromLocation?.city || fromLocation?.label || 'N/A';
        const toCity = toLocation?.city || toLocation?.label || 'N/A';
        return (
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{fromCity}</span>
            <span className="text-muted-foreground">→</span>
            <span className="font-medium">{toCity}</span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => {
        return getStatusBadge(row.original.status);
      },
    },
    {
      id: 'offers',
      header: 'Offres',
      accessorKey: 'offers_count',
      cell: ({ row }) => {
        const count = row.original.offers_count || 0;
        return (
          <Badge variant="outline">{count}</Badge>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const request = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/admin/clients/${request.client?.id || request.client}/requests/${request.id}`)}>
                <Eye className="w-4 h-4 mr-2" />
                Voir détails
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setUpdateDialog({ open: true, request, newStatus: '' })}>
                <FileText className="w-4 h-4 mr-2" />
                Modifier statut
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteDialog({ open: true, request })}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Filtres et recherche */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par ID, client, ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {constants?.transport_request_status?.map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={loadRequests} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Tableau */}
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={filteredRequests}
            loading={loading}
            emptyMessage="Aucune demande trouvée"
          />
        </CardContent>
      </Card>

      {/* Dialog pour modifier le statut */}
      <AlertDialog open={updateDialog.open} onOpenChange={(open) => setUpdateDialog({ ...updateDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifier le statut de la demande</AlertDialogTitle>
            <AlertDialogDescription>
              Sélectionnez le nouveau statut pour la demande {updateDialog.request?.logiconnect_id}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select
              value={updateDialog.newStatus}
              onValueChange={(value) => setUpdateDialog({ ...updateDialog, newStatus: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un statut" />
              </SelectTrigger>
              <SelectContent>
                {constants?.request_status?.map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpdateStatus}
              disabled={!updateDialog.newStatus}
            >
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog pour supprimer la demande */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-destructive/10 rounded-full">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <AlertDialogTitle className="text-xl font-bold text-destructive">
                ⚠️ ATTENTION - SUPPRESSION DÉFINITIVE
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="font-semibold text-foreground">
                Vous êtes sur le point de supprimer définitivement cette demande de transport.
              </p>
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                <p className="font-medium text-destructive">
                  <strong>ID de la demande :</strong> {deleteDialog.request?.logiconnect_id || deleteDialog.request?.id?.slice(-8) || 'N/A'}
                </p>
                {deleteDialog.request?.client_detail?.company_name && (
                  <p className="text-sm">
                    <strong>Client :</strong> {deleteDialog.request.client_detail.company_name}
                  </p>
                )}
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                  ⚠️ Cette action est irréversible !
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>La demande sera supprimée de manière permanente</li>
                  <li>Toutes les données associées seront perdues</li>
                  <li>Cette action ne peut pas être annulée</li>
                </ul>
              </div>
              <p className="text-sm font-medium text-foreground">
                Êtes-vous absolument certain de vouloir continuer ?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRequest}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Oui, supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OperationsRequestsList;
